/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  breakpoints,
  BackgroundColors,
  remapDeprecatedColors,
} from '../TestimonialSolo/TestimonialMapping';
import { css } from '@emotion/react';
import { Colors, Typography, Avatar } from '../../../index';

import {
  TestimonialSoloProps as TestimonialProps,
  TestimonialSolo,
} from '../TestimonialSolo';

export type Props = {
  /**
   * Array of testimonial elements.
   * Fields:
   *
   * `quote`: Text content displayed on the top or below the avatar.
   *
   * `author`: Text content displayed below quote.
   *
   * `company`: Text content displayed below author initials.
   *
   * `hasAuthorAvatar?`: Whether or not Testimonial has author avatar. Default value is `False`.
   *
   * `avatarUrl`: Author avatar url. This field is not required.
   */
  testimonials: Array<Omit<TestimonialProps, 'backgroundColor'>>;
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava10' | 'ocean10' | 'sun10' | 'white' | 'cream' | 'neutral100' | 'acidWash' | 'neutral200'.
   */
  backgroundColor?: BackgroundColors;
  /**
   * Number of columns to be displayed.
   */
  numberOfColumns: 2 | 3;
};

const CommonStyles = labelStyles('TestimonialCommon', {
  quote: css`
    ${Typography.smallPrint1}
    color: ${Colors.neutral800};
    margin-bottom: 10px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-bottom: 20px;
      ${Typography.paragraph3}
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      margin-bottom: 20px;
      ${Typography.paragraph1}
      color: ${Colors.neutral700};
    }
  `,
  author: css`
    ${Typography.smallPrint1Bold}
    color: ${Colors.neutral700};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.paragraph3Bold}
    }
  `,
  company: css`
    ${Typography.smallPrint1}
    color: ${Colors.neutral700};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.paragraph3}
    }
  `,
});

const TwoColumnStyles = labelStyles('TestimonialTwoColumn', {
  outerBox: (backgroundColor: BackgroundColors = 'neutral100') => css`
    background-color: ${Colors[remapDeprecatedColors(backgroundColor)]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 72px 30px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 82px 0px;
    }
  `,
  grid: css`
    display: grid;
    grid-template-rows: auto;
    row-gap: 25px;
    max-width: 1170px;
    justify-content: center;
    margin: 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      grid-template-columns: auto 1px auto;
      column-gap: 15px;
      @media screen and (min-width: ${breakpoints.desktop}px) {
        column-gap: 115px;
      }
    }
  `,
  content: css`
    margin: 0 auto;
    text-align: center;
    max-width: 336px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 339px;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 370px;
    }
  `,
  avatarContainer: css`
    margin-bottom: 10px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-bottom: 20px;
    }
  `,
  border: css`
    @media screen and (min-width: ${breakpoints.tablet}px) {
      width: 1px;
      background-color: ${Colors.neutral400};
    }
  `,
});

const ThreeColumnStyles = labelStyles('TestimonialThreeColumn', {
  outerBox: (backgroundColor: BackgroundColors = 'neutral100') => css`
    background-color: ${Colors[remapDeprecatedColors(backgroundColor)]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 40px 0px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      padding: 107px 0px;
    }
  `,
  grid: css`
    display: grid;
    grid-template-rows: auto;
    row-gap: 40px;
    max-width: 1170px;
    justify-content: center;
    margin: 0 auto;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      row-gap: 50px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      grid-template-columns: auto auto auto;
      column-gap: 30px;
    }
  `,
  content: css`
    margin: 0 auto;
    text-align: left;
    max-width: 336px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 462px;
    }

    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 370px;
    }
  `,
  innerFlex: css`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 20px;
  `,
});

const renderContentForTwoColumn = (props: Props, index: number) => {
  return (
    <div css={TwoColumnStyles.content}>
      {props.testimonials[index].hasAuthorAvatar && (
        <div css={TwoColumnStyles.avatarContainer}>
          <Avatar
            data-testId="Avatar"
            name={props.testimonials[index].author}
            url={props.testimonials[index].avatarUrl}
            size={40}
          />
        </div>
      )}
      <div css={CommonStyles.quote}>{props.testimonials[index].quote}</div>
      <p css={CommonStyles.author}>{props.testimonials[index].author}</p>
      <p css={CommonStyles.company}>{props.testimonials[index].company}</p>
    </div>
  );
};

const renderTwoColumn = (props: Props) => {
  return (
    <div css={TwoColumnStyles.outerBox(props.backgroundColor)}>
      <div css={TwoColumnStyles.grid}>
        {renderContentForTwoColumn(props, 0)}
        <div css={TwoColumnStyles.border} />
        {renderContentForTwoColumn(props, 1)}
      </div>
    </div>
  );
};

const renderThreeColumn = (props: Props) => {
  return (
    <div css={ThreeColumnStyles.outerBox(props.backgroundColor)}>
      <div css={ThreeColumnStyles.grid}>
        {props.testimonials
          .slice(0, props.numberOfColumns)
          .map((testimonial, index) => (
            <div key={index} css={ThreeColumnStyles.content}>
              <div css={CommonStyles.quote}>{testimonial.quote}</div>
              {testimonial.hasAuthorAvatar ? (
                <div css={ThreeColumnStyles.innerFlex}>
                  <div css={TwoColumnStyles.avatarContainer}>
                    <Avatar
                      data-testId="Avatar"
                      name={testimonial.author}
                      url={testimonial.avatarUrl}
                      size={40}
                    />
                  </div>
                  <div>
                    <p css={CommonStyles.author}>{testimonial.author}</p>
                    <p css={CommonStyles.company}>{testimonial.company}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p css={CommonStyles.author}>{testimonial.author}</p>
                  <p css={CommonStyles.company}>{testimonial.company}</p>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export const TestimonialMultipleColumn = (props: Props) => {
  /*
    If the the length of testimonial array is 1, we are rendering Solo Testimonial.
  */
  if (props.testimonials.length === 1) {
    const testimonial = props.testimonials[0];
    return (
      <TestimonialSolo
        backgroundColor={props.backgroundColor}
        avatarUrl={testimonial.avatarUrl}
        company={testimonial.company}
        quote={testimonial.quote}
        author={testimonial.author}
      />
    );
  }
  const renderType =
    props.numberOfColumns === 2 ? renderTwoColumn : renderThreeColumn;
  return renderType(props);
};
